import React, {useState, useEffect, useCallback} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import Icon from './icons/Icon';
import MiaPlatformSelector from './MiaPlatformSelector';
import MiaPlatformDataService from '../services/MiaPlatformDataService';
import './MobileCodePage.css';

/**
 * Mobile Code Page Component
 * Full-screen mobile experience for browsing and viewing code
 * Optimized for touch interactions and small screens
 */
const MobileCodePage = ({
  isOpen,
  onClose,
  initialTab = 'project',
  onAskAI
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [fileLoading, setFileLoading] = useState(false);
  
  // Mia Platform state
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(() => {
    const saved = localStorage.getItem('mia_selectedTenant');
    return saved ? JSON.parse(saved) : null;
  });
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [tenantsError, setTenantsError] = useState(null);
  
  const [projects, setProjects] = useState([]);
  const [projectsPagination, setProjectsPagination] = useState(null);
  const [projectsCurrentPage, setProjectsCurrentPage] = useState(1);
  const [projectsSearchQuery, setProjectsSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState(() => {
    const saved = localStorage.getItem('mia_selectedProject');
    return saved ? JSON.parse(saved) : null;
  });
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState(null);
  
  const [revisions, setRevisions] = useState([]);
  const [selectedRevision, setSelectedRevision] = useState(() => {
    const saved = localStorage.getItem('mia_selectedRevision');
    return saved ? JSON.parse(saved) : null;
  });
  const [revisionsLoading, setRevisionsLoading] = useState(false);
  const [revisionsError, setRevisionsError] = useState(null);
  
  const [projectConfig, setProjectConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [containers, setContainers] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [collections, setCollections] = useState([]);
  const [configMaps, setConfigMaps] = useState([]);
  const [secrets, setSecrets] = useState([]);
  
  const [selectedResource, setSelectedResource] = useState(null);
  const [selectedResourceType, setSelectedResourceType] = useState(null);
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  
  // Create service state
  const [showCreateServiceModal, setShowCreateServiceModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [serviceName, setServiceName] = useState('');
  const [serviceDescription, setServiceDescription] = useState('');
  const [createServiceLoading, setCreateServiceLoading] = useState(false);
  
  // Toast notification state
  const [toast, setToast] = useState(null);
  
  const showToast = (message, type = 'info') => {
    setToast({message, type});
    setTimeout(() => setToast(null), 3000);
  };

  // Load Mia Flow templates
  const loadTemplates = async () => {
    if (!selectedTenant) return;
    
    setTemplatesLoading(true);
    try {
      const result = await MiaPlatformDataService.getMiaFlowTemplates(selectedTenant.id);
      if (result.success) {
        setTemplates(result.data.items || []);
      } else {
        showToast(result.error || 'Failed to load templates', 'error');
        setTemplates([]);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      showToast('Failed to load templates', 'error');
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Handle create service modal open
  const handleCreateServiceClick = () => {
    setShowCreateServiceModal(true);
    if (templates.length === 0) {
      loadTemplates();
    }
  };

  // Handle create service
  const handleCreateService = async () => {
    if (!serviceName.trim()) {
      showToast('Service name is required', 'error');
      return;
    }
    if (!selectedTemplate) {
      showToast('Please select a template', 'error');
      return;
    }
    if (!selectedProject || !selectedRevision) {
      showToast('Project and revision must be selected', 'error');
      return;
    }

    setCreateServiceLoading(true);
    try {
      const result = await MiaPlatformDataService.createServiceFromTemplate({
        projectId: selectedProject.id,
        revisionId: selectedRevision.id,
        serviceName: serviceName.trim(),
        serviceDescription: serviceDescription.trim(),
        templateId: selectedTemplate.id,
        templateItemId: selectedTemplate.itemId,
        templateTenantId: selectedTemplate.tenantId,
        templateVersion: selectedTemplate.version,
      });

      if (result.success) {
        showToast('Service created successfully!', 'success');
        setShowCreateServiceModal(false);
        setServiceName('');
        setServiceDescription('');
        setSelectedTemplate(null);
        // Reload project config to show new service
        loadProjectConfig();
      } else {
        showToast(result.error || 'Failed to create service', 'error');
      }
    } catch (error) {
      console.error('Error creating service:', error);
      // Show the actual error message if available
      const errorMsg = error.message || 'Failed to create service';
      showToast(errorMsg, 'error');
    } finally {
      setCreateServiceLoading(false);
    }
  };

  // Load data
  useEffect(() => {
    if (isOpen && tenants.length === 0 && !tenantsLoading) {
      loadTenants();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedTenant && isOpen) {
      loadProjects(selectedTenant.id, '', 1, false);
    }
  }, [selectedTenant, isOpen]);

  useEffect(() => {
    if (selectedProject && isOpen) {
      loadRevisions(selectedProject.id);
    }
  }, [selectedProject, isOpen]);

  useEffect(() => {
    if (selectedProject && selectedRevision && isOpen) {
      loadProjectConfig();
    }
  }, [selectedProject, selectedRevision, isOpen]);

  // Persist selections
  useEffect(() => {
    if (selectedTenant) {
      localStorage.setItem('mia_selectedTenant', JSON.stringify(selectedTenant));
    }
  }, [selectedTenant]);

  useEffect(() => {
    if (selectedProject) {
      localStorage.setItem('mia_selectedProject', JSON.stringify(selectedProject));
    }
  }, [selectedProject]);

  useEffect(() => {
    if (selectedRevision) {
      localStorage.setItem('mia_selectedRevision', JSON.stringify(selectedRevision));
    }
  }, [selectedRevision]);

  const loadTenants = async () => {
    try {
      setTenantsLoading(true);
      setTenantsError(null);
      const tenantsData = await MiaPlatformDataService.getTenants();
      const formattedTenants = tenantsData.map(tenant => 
        MiaPlatformDataService.formatTenant(tenant)
      );
      setTenants(formattedTenants);
    } catch (error) {
      console.error('Failed to load tenants:', error);
      setTenantsError(error.message || 'Failed to load tenants');
    } finally {
      setTenantsLoading(false);
    }
  };

  const loadProjects = async (tenantId, searchQuery = '', page = 1, append = false) => {
    try {
      setProjectsLoading(true);
      setProjectsError(null);
      const result = await MiaPlatformDataService.getProjects(tenantId, searchQuery, page);
      const formattedProjects = result.data.map(project => 
        MiaPlatformDataService.formatProject(project)
      );
      if (append) {
        setProjects(prev => [...prev, ...formattedProjects]);
      } else {
        setProjects(formattedProjects);
      }
      setProjectsPagination(result.pagination);
      setProjectsCurrentPage(page);
      if (!append && formattedProjects.length === 1) {
        setSelectedProject(formattedProjects[0]);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
      setProjectsError(error.message || 'Failed to load projects');
    } finally {
      setProjectsLoading(false);
    }
  };

  const loadRevisions = async (projectId) => {
    try {
      setRevisionsLoading(true);
      setRevisionsError(null);
      const revisionsData = await MiaPlatformDataService.getRevisions(projectId);
      const formattedRevisions = revisionsData.map(revision => 
        MiaPlatformDataService.formatRevision(revision)
      );
      setRevisions(formattedRevisions);
      if (formattedRevisions.length > 0 && !selectedRevision) {
        setSelectedRevision(formattedRevisions[0]);
      }
    } catch (error) {
      console.error('Failed to load revisions:', error);
      setRevisionsError(error.message || 'Failed to load revisions');
    } finally {
      setRevisionsLoading(false);
    }
  };

  const loadProjectConfig = async () => {
    try {
      setConfigLoading(true);
      const config = await MiaPlatformDataService.getMicroservices(
        selectedProject.id,
        selectedRevision.id
      );
      setProjectConfig(config);
      
      // Extract all resource types
      if (config?.services) {
        const servicesList = Object.entries(config.services).map(([name, service]) => 
          MiaPlatformDataService.formatMicroservice(name, service)
        );
        setServices(servicesList);
      }
      
      if (config?.unsecretedVariables?.services) {
        const containersList = Object.entries(config.unsecretedVariables.services).map(([name, container]) => ({
          ...container,
          name,
          id: name
        }));
        setContainers(containersList);
      }
      
      if (config?.endpoints) {
        const endpointsList = Object.entries(config.endpoints).map(([id, endpoint]) => ({
          ...endpoint,
          id,
          name: endpoint.basePath || id
        }));
        setEndpoints(endpointsList);
      }
      
      if (config?.collections) {
        const collectionsList = Object.entries(config.collections).map(([name, collection]) => ({
          ...collection,
          name,
          id: name
        }));
        setCollections(collectionsList);
      }
      
      if (config?.configMaps) {
        const configMapsList = Object.entries(config.configMaps).map(([name, configMap]) => ({
          ...configMap,
          name,
          id: name
        }));
        setConfigMaps(configMapsList);
      }
      
      if (config?.secrets) {
        const secretsList = Object.entries(config.secrets).map(([name, secret]) => ({
          ...secret,
          name,
          id: name
        }));
        setSecrets(secretsList);
      }
    } catch (error) {
      console.error('Failed to load project config:', error);
    } finally {
      setConfigLoading(false);
    }
  };

  const handleTenantSelect = (tenant) => {
    setSelectedTenant(tenant);
    setSelectedProject(null);
    setSelectedRevision(null);
    setProjects([]);
    setRevisions([]);
    setProjectConfig(null);
    setServices([]);
  };

  const handleProjectSearch = useCallback((searchQuery) => {
    if (selectedTenant) {
      setProjectsSearchQuery(searchQuery);
      setProjectsCurrentPage(1);
      // Only clear selection if user is actively searching (not when clearing search)
      // Don't clear when searchQuery is empty (programmatic clear after selection)
      if (searchQuery && searchQuery !== projectsSearchQuery) {
        setSelectedProject(null);
        setSelectedRevision(null);
        setRevisions([]);
      }
      loadProjects(selectedTenant.id, searchQuery, 1, false);
    }
  }, [selectedTenant, projectsSearchQuery]);

  const handleLoadMoreProjects = useCallback(() => {
    if (selectedTenant && projectsPagination?.has_more && !projectsLoading) {
      const nextPage = projectsCurrentPage + 1;
      loadProjects(selectedTenant.id, projectsSearchQuery, nextPage, true);
    }
  }, [selectedTenant, projectsPagination, projectsLoading, projectsCurrentPage, projectsSearchQuery]);

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    setSelectedRevision(null);
    setRevisions([]);
    setProjectConfig(null);
    setServices([]);
  };

  const handleRevisionSelect = (revision) => {
    setSelectedRevision(revision);
    setProjectConfig(null);
    setServices([]);
  };

  const handleResourceClick = (resource, type) => {
    setSelectedResource(resource);
    setSelectedResourceType(type);
    
    if (type === 'source-code' && resource.repositoryUrl) {
      // Load file tree for this repository
      loadFileTree(resource);
    } else {
      setActiveTab('code');
    }
  };
  
  const loadFileTree = async (service) => {
    if (!service.repositoryUrl) {
      console.warn('No repository URL found for service:', service.name);
      setFiles([]);
      showToast('No repository URL configured', 'error');
      return;
    }

    setFilesLoading(true);
    setActiveTab('code');
    
    try {
      // Extract GitLab project info from repository URL
      const url = new URL(service.repositoryUrl);
      let pathParts = url.pathname.split('/').filter(Boolean);
      
      // Remove .git extension if present
      if (pathParts.length > 0) {
        const lastPart = pathParts[pathParts.length - 1];
        if (lastPart.endsWith('.git')) {
          pathParts[pathParts.length - 1] = lastPart.slice(0, -4);
        }
      }
      
      console.log('Loading file tree for:', {
        repositoryUrl: service.repositoryUrl,
        pathParts,
        selectedRevision: selectedRevision?.name
      });
      
      // GitLab URL format: https://git.domain/namespace/group/project
      // We need to join all path parts except the first (which might be like 'gitlab' or empty)
      if (pathParts.length >= 2) {
        // For URLs like git.tools.mia-platform.eu/clients/mia-platform/demo/.../project
        // Join all parts to get the full project path
        const projectId = pathParts.join('/');

        console.log('Fetching repository tree with:', {projectId, ref: selectedRevision?.name || 'main'});

        // Get repository tree from GitLab (auth via cookies)
        const tree = await unifiedGitDataService.getRepositoryTree(
          projectId,
          selectedResource.repositoryUrl,
          '',  // root path
          selectedRevision?.name || 'main'
        );
        
        console.log('Received tree data:', tree);
        
        // Filter to only show files (blobs), not directories (trees)
        const fileItems = tree.filter(item => item.type === 'blob');
        
        console.log('Filtered files:', fileItems.length);
        
        // Format the tree data
        const formattedFiles = fileItems.map(item => ({
          path: item.path,
          type: item.type,
          size: item.size ? formatFileSize(item.size) : 'N/A',
          id: item.id
        }));
        
        setFiles(formattedFiles);
        
        if (formattedFiles.length === 0) {
          showToast('No files found in repository', 'info');
        } else {
          showToast(`Loaded ${formattedFiles.length} files`, 'success');
        }
      } else {
        throw new Error('Invalid GitLab repository URL format');
      }
    } catch (error) {
      console.error('Failed to load file tree:', error);
      
      // Check if it's an authentication error
      if (error.message.includes('Not Found') || error.message.includes('404')) {
        showToast(`GitLab project not found. Please configure GitLab token in BFF settings.`, 'error');
      } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        showToast(`GitLab authentication failed. Please configure your GitLab token.`, 'error');
      } else {
        showToast(`Failed to load files: ${error.message}`, 'error');
      }
      
      // Show a helpful message instead of empty state
      setFiles([
        {
          path: '⚠️ GitLab Configuration Required',
          type: 'blob',
          size: 'See instructions',
          id: 'help-1',
          isPlaceholder: true
        },
        {
          path: '1. Set GITLAB_API_TOKEN in BFF .env file',
          type: 'blob',
          size: 'Configuration',
          id: 'help-2',
          isPlaceholder: true
        },
        {
          path: '2. Set GITLAB_BASE_URL if using self-hosted',
          type: 'blob',
          size: 'Configuration',
          id: 'help-3',
          isPlaceholder: true
        },
        {
          path: '3. Restart BFF server',
          type: 'blob',
          size: 'Configuration',
          id: 'help-4',
          isPlaceholder: true
        }
      ]);
    } finally {
      setFilesLoading(false);
    }
  };
  
  // Helper function to format file sizes
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 10) / 10 + ' ' + sizes[i];
  };
  
  const handleFileClick = async (file) => {
    if (!selectedResource?.repositoryUrl || !file.path) return;
    
    // Ignore placeholder files
    if (file.isPlaceholder) {
      showToast('GitLab integration is not configured. Contact your administrator.', 'info');
      return;
    }
    
    setSelectedFile({ ...file, name: file.path });
    
    try {
      // Extract GitLab project info
      const url = new URL(selectedResource.repositoryUrl);
      let pathParts = url.pathname.split('/').filter(Boolean);
      
      // Remove .git extension if present
      if (pathParts.length > 0) {
        const lastPart = pathParts[pathParts.length - 1];
        if (lastPart.endsWith('.git')) {
          pathParts[pathParts.length - 1] = lastPart.slice(0, -4);
        }
      }
      
      if (pathParts.length >= 2) {
        const projectId = pathParts.join('/');

        // Get file content from GitLab (auth via cookies)
        const content = await unifiedGitDataService.getFileContent(
          projectId,
          selectedResource.repositoryUrl,
          file.path,
          selectedRevision?.name || 'main'
        );
        
        setFileContent(content);
      }
    } catch (error) {
      console.error('Failed to load file content:', error);
      setFileContent(`// Error loading file: ${file.path}\n// ${error.message}\n\n// Please check your GitLab connection and try again.`);
    }
  };

  const handleAskAIAboutService = (service, event) => {
    event.stopPropagation(); // Prevent card click
    const prompt = `I'm looking at the "${service.name}" service in my project "${selectedProject.name}". Can you help me understand or modify this service?\n\nService details:\n- Type: ${service.type}\n- Docker Image: ${service.dockerImage || 'N/A'}\n- Replicas: ${service.replicas || 1}\n\nWhat would you like to know or change about this service?`;
    
    if (onAskAI) {
      onAskAI(prompt);
    }
    onClose(); // Close the code page to return to chat
  };

  if (!isOpen) return null;

  return (
    <div className="mobile-code-page">
      {/* Header */}
      <div className="mobile-code-header">
        <button
          className="back-button"
          onClick={onClose}
          type="button"
          aria-label="Go back"
        >
          <Icon name="arrow-left" size={24} />
        </button>
        <button
          className="page-title-button"
          onClick={onClose}
          type="button"
          aria-label="Go back to chat"
        >
          <Icon name="code" size={24} />
          {'AI Coder'}
        </button>
        <div className="header-spacer" />
      </div>

      {/* Tab Navigation */}
      <div className="mobile-code-tabs">
        <button
          className={`tab-button ${activeTab === 'project' ? 'active' : ''}`}
          onClick={() => setActiveTab('project')}
          type="button"
        >
          <Icon name="folder" size={20} />
          {'Project'}
        </button>
        <button
          className={`tab-button ${activeTab === 'resources' ? 'active' : ''}`}
          onClick={() => setActiveTab('resources')}
          type="button"
          disabled={!projectConfig}
        >
          <Icon name="layers" size={20} />
          {'Resources'}
        </button>
        <button
          className={`tab-button ${activeTab === 'code' ? 'active' : ''}`}
          onClick={() => setActiveTab('code')}
          type="button"
          disabled={!selectedFile}
        >
          <Icon name="document" size={20} />
          {'Code'}
        </button>
      </div>

      {/* Content */}
      <div className="mobile-code-content">
        {/* Project Tab */}
        {activeTab === 'project' && (
          <div className="tab-content">
            <div className="selector-section">
              <label className="selector-label">Organization</label>
              <MiaPlatformSelector
                emptyMessage="No organizations found"
                error={tenantsError}
                icon={<Icon name="organization" size={20} />}
                items={tenants}
                loading={tenantsLoading}
                onSelect={handleTenantSelect}
                placeholder="Select an organization"
                searchable
                selectedItem={selectedTenant}
              />
            </div>

            <div className="selector-section">
              <label className="selector-label">Project</label>
              <MiaPlatformSelector
                emptyMessage={selectedTenant ? 'No projects found' : 'Select an organization first'}
                error={projectsError}
                hasMore={projectsPagination?.has_more || false}
                icon={<Icon name="folder" size={20} />}
                items={projects}
                loading={projectsLoading}
                onLoadMore={handleLoadMoreProjects}
                onSearch={handleProjectSearch}
                onSelect={handleProjectSelect}
                placeholder="Select a project"
                searchable
                selectedItem={selectedProject}
              />
            </div>

            <div className="selector-section">
              <label className="selector-label">Revision</label>
              <MiaPlatformSelector
                emptyMessage={selectedProject ? 'No revisions found' : 'Select a project first'}
                error={revisionsError}
                icon={<Icon name="git-branch" size={20} />}
                items={revisions}
                loading={revisionsLoading}
                onSelect={handleRevisionSelect}
                placeholder="Select a revision"
                searchable={false}
                selectedItem={selectedRevision}
              />
            </div>

            {selectedProject && selectedRevision && (
              <div className="project-summary-card">
                <h3>Project Summary</h3>
                <div className="summary-grid">
                  <div className="summary-item">
                    <Icon name="organization" size={18} />
                    <div>
                      <div className="item-label">Organization</div>
                      <div className="item-value">{selectedTenant?.name}</div>
                    </div>
                  </div>
                  <div className="summary-item">
                    <Icon name="folder" size={18} />
                    <div>
                      <div className="item-label">Project</div>
                      <div className="item-value">{selectedProject?.name}</div>
                    </div>
                  </div>
                  <div className="summary-item">
                    <Icon name="git-branch" size={18} />
                    <div>
                      <div className="item-label">Revision</div>
                      <div className="item-value">{selectedRevision?.name}</div>
                    </div>
                  </div>
                </div>
                <button
                  className="view-resources-btn"
                  onClick={() => setActiveTab('resources')}
                  type="button"
                  disabled={configLoading}
                >
                  <Icon name="layers" size={20} />
                  {configLoading ? 'Loading...' : 'View Resources'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Resources Tab */}
        {activeTab === 'resources' && (
          <div className="tab-content">
            <div className="resources-header">
              <h2>Project Resources</h2>
              <p className="resources-description">
                Browse all resources in your project
              </p>
            </div>

            {configLoading ? (
              <div className="loading-state">
                <Icon name="spinner" size={32} />
                <p>Loading resources...</p>
              </div>
            ) : (
              <div className="resource-categories">
                {/* Source Code */}
                <div className="resource-category">
                  <div className="category-header">
                    <Icon name="code" size={20} />
                    <h3>Source Code</h3>
                    <span className="category-count">
                      {services.filter(s => s.repositoryUrl || s.type === 'custom').length}
                    </span>
                    <button 
                      className="add-resource-btn"
                      onClick={handleCreateServiceClick}
                      title="Create new service"
                      type="button"
                    >
                      <Icon name="plus" size={18} />
                    </button>
                  </div>
                  {services.filter(s => s.repositoryUrl || s.type === 'custom').length > 0 ? (
                    <div className="resource-list">
                      {services.filter(s => s.repositoryUrl || s.type === 'custom').map((service, index) => (
                        <button
                          key={index}
                          className="resource-item"
                          onClick={() => handleResourceClick(service, 'source-code')}
                          type="button"
                        >
                          <div className="resource-icon">
                            <Icon name="file-code" size={20} />
                          </div>
                          <div className="resource-info">
                            <div className="resource-name">{service.name}</div>
                            <div className="resource-meta">
                              {service.repositoryUrl ? new URL(service.repositoryUrl).pathname : 'Custom service'}
                            </div>
                          </div>
                          <Icon name="chevron-right" size={18} />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p>No source code items yet. Click + to create one.</p>
                    </div>
                  )}
                </div>

                {/* Containers */}
                <div className="resource-category">
                  <div className="category-header">
                    <Icon name="box" size={20} />
                    <h3>Containers</h3>
                    <span className="category-count">{containers.length}</span>
                    <button 
                      className="add-resource-btn"
                      onClick={() => showToast('Container creation coming soon', 'info')}
                      title="Create new container"
                      type="button"
                    >
                      <Icon name="plus" size={18} />
                    </button>
                  </div>
                  {containers.length > 0 ? (
                    <div className="resource-list">
                      {containers.map((container, index) => (
                        <button
                          key={index}
                          className="resource-item"
                          onClick={() => handleResourceClick(container, 'container')}
                          type="button"
                        >
                          <div className="resource-icon">
                            <Icon name="package" size={20} />
                          </div>
                          <div className="resource-info">
                            <div className="resource-name">{container.name}</div>
                            <div className="resource-meta">Container configuration</div>
                          </div>
                          <Icon name="chevron-right" size={18} />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p>No containers yet. Click + to create one.</p>
                    </div>
                  )}
                </div>

                {/* Endpoints */}
                <div className="resource-category">
                  <div className="category-header">
                    <Icon name="link" size={20} />
                    <h3>Endpoints</h3>
                    <span className="category-count">{endpoints.length}</span>
                    <button 
                      className="add-resource-btn"
                      onClick={() => showToast('Endpoint creation coming soon', 'info')}
                      title="Create new endpoint"
                      type="button"
                    >
                      <Icon name="plus" size={18} />
                    </button>
                  </div>
                  {endpoints.length > 0 ? (
                    <div className="resource-list">
                      {endpoints.map((endpoint, index) => (
                        <button
                          key={index}
                          className="resource-item"
                          onClick={() => handleResourceClick(endpoint, 'endpoint')}
                          type="button"
                        >
                          <div className="resource-icon">
                            <Icon name="globe" size={20} />
                          </div>
                          <div className="resource-info">
                            <div className="resource-name">{endpoint.name}</div>
                            <div className="resource-meta">{endpoint.type || 'API endpoint'}</div>
                          </div>
                          <Icon name="chevron-right" size={18} />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p>No endpoints yet. Click + to create one.</p>
                    </div>
                  )}
                </div>

                {/* Collections */}
                <div className="resource-category">
                  <div className="category-header">
                    <Icon name="database" size={20} />
                    <h3>Collections</h3>
                    <span className="category-count">{collections.length}</span>
                    <button 
                      className="add-resource-btn"
                      onClick={() => showToast('Collection creation coming soon', 'info')}
                      title="Create new collection"
                      type="button"
                    >
                      <Icon name="plus" size={18} />
                    </button>
                  </div>
                  {collections.length > 0 ? (
                    <div className="resource-list">
                      {collections.map((collection, index) => (
                        <button
                          key={index}
                          className="resource-item"
                          onClick={() => handleResourceClick(collection, 'collection')}
                          type="button"
                        >
                          <div className="resource-icon">
                            <Icon name="table" size={20} />
                          </div>
                          <div className="resource-info">
                            <div className="resource-name">{collection.name}</div>
                            <div className="resource-meta">
                              {collection.fields?.length || 0} fields
                            </div>
                          </div>
                          <Icon name="chevron-right" size={18} />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p>No collections yet. Click + to create one.</p>
                    </div>
                  )}
                </div>

                {/* Fast Data */}
                <div className="resource-category">
                  <div className="category-header">
                    <Icon name="zap" size={20} />
                    <h3>Fast Data</h3>
                    <span className="category-count">0</span>
                    <button 
                      className="add-resource-btn"
                      onClick={() => showToast('Fast Data creation coming soon', 'info')}
                      title="Create new Fast Data system"
                      type="button"
                    >
                      <Icon name="plus" size={18} />
                    </button>
                  </div>
                  <div className="empty-state">
                    <p>No Fast Data systems yet. Click + to create one.</p>
                  </div>
                </div>

                {/* Flow Manager */}
                <div className="resource-category">
                  <div className="category-header">
                    <Icon name="git-merge" size={20} />
                    <h3>Flow Manager</h3>
                    <span className="category-count">0</span>
                    <button 
                      className="add-resource-btn"
                      onClick={() => showToast('Flow Manager creation coming soon', 'info')}
                      title="Create new flow"
                      type="button"
                    >
                      <Icon name="plus" size={18} />
                    </button>
                  </div>
                  <div className="empty-state">
                    <p>No flows yet. Click + to create one.</p>
                  </div>
                </div>

                {/* Config & Secrets */}
                <div className="resource-category">
                  <div className="category-header">
                    <Icon name="settings" size={20} />
                    <h3>Config & Secrets</h3>
                    <span className="category-count">{configMaps.length + secrets.length}</span>
                    <button 
                      className="add-resource-btn"
                      onClick={() => showToast('Config/Secret creation coming soon', 'info')}
                      title="Create new config or secret"
                      type="button"
                    >
                      <Icon name="plus" size={18} />
                    </button>
                  </div>
                  {(configMaps.length > 0 || secrets.length > 0) ? (
                    <div className="resource-list">
                      {configMaps.map((configMap, index) => (
                        <button
                          key={`cm-${index}`}
                          className="resource-item"
                          onClick={() => handleResourceClick(configMap, 'configmap')}
                          type="button"
                        >
                          <div className="resource-icon">
                            <Icon name="file-text" size={20} />
                          </div>
                          <div className="resource-info">
                            <div className="resource-name">{configMap.name}</div>
                            <div className="resource-meta">Config Map</div>
                          </div>
                          <Icon name="chevron-right" size={18} />
                        </button>
                      ))}
                      {secrets.map((secret, index) => (
                        <button
                          key={`s-${index}`}
                          className="resource-item"
                          onClick={() => handleResourceClick(secret, 'secret')}
                          type="button"
                        >
                          <div className="resource-icon">
                            <Icon name="lock" size={20} />
                          </div>
                          <div className="resource-info">
                            <div className="resource-name">{secret.name}</div>
                            <div className="resource-meta">Secret</div>
                          </div>
                          <Icon name="chevron-right" size={18} />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p>No configs or secrets yet. Click + to create one.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Code Tab */}
        {activeTab === 'code' && (
          <div className="tab-content code-view">
            {selectedFile ? (
              <>
                <div className="code-header">
                  <button 
                    className="back-button" 
                    onClick={() => {
                      setSelectedFile(null);
                      setFileContent('');
                    }}
                    type="button"
                  >
                    <Icon name="arrow-left" size={16} />
                  </button>
                  <Icon name="file-code" size={20} />
                  <h3>{selectedFile.path}</h3>
                </div>
                <pre className="code-content">
                  <code>{fileContent}</code>
                </pre>
                <button
                  className="floating-ai-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const prompt = `Can you help me with the file "${selectedFile.path}" from ${selectedResource?.name}?\n\nHere's the code:\n\n${fileContent}`;
                    handleAskAI(prompt);
                  }}
                  type="button"
                  title="Ask AI about this code"
                >
                  <Icon name="message-circle" size={20} />
                  <span>Ask AI</span>
                </button>
              </>
            ) : selectedResource && selectedResourceType === 'source-code' ? (
              <>
                <div className="code-header">
                  <button 
                    className="back-button" 
                    onClick={() => {
                      setSelectedResource(null);
                      setSelectedResourceType(null);
                      setFiles([]);
                      setActiveTab('resources');
                    }}
                    type="button"
                  >
                    <Icon name="arrow-left" size={20} />
                    <span>Back to Resources</span>
                  </button>
                  <div className="header-title">
                    <h2>{selectedResource.name}</h2>
                    <p className="resource-type-label">Source Code</p>
                  </div>
                  <div className="git-actions">
                    <button 
                      className="git-action-btn"
                      onClick={() => {
                        showToast('Pulling latest changes...', 'info');
                        loadFileTree(selectedResource);
                      }}
                      type="button"
                      title="Pull latest changes"
                    >
                      <Icon name="download" size={18} />
                    </button>
                    <button 
                      className="git-action-btn"
                      onClick={() => {
                        showToast('Push to repository...', 'info');
                        // TODO: Implement git push
                      }}
                      type="button"
                      title="Push changes"
                    >
                      <Icon name="upload" size={18} />
                    </button>
                  </div>
                </div>

                {filesLoading ? (
                  <div className="loading-state">
                    <Icon name="spinner" size={32} />
                    <p>Loading files...</p>
                  </div>
                ) : files.length > 0 ? (
                  <div className="file-tree">
                    <div className="file-tree-header">
                      <Icon name="folder" size={18} />
                      <span>{files.length} files</span>
                    </div>
                    <div className="file-list">
                      {files.map((file, index) => (
                        <button
                          key={index}
                          className="file-item"
                          onClick={() => handleFileClick(file)}
                          type="button"
                        >
                          <Icon 
                            name={
                              file.path.endsWith('.js') || file.path.endsWith('.jsx') ? 'file-code' :
                              file.path.endsWith('.json') ? 'braces' :
                              file.path.endsWith('.md') ? 'file-text' :
                              file.path.endsWith('.css') ? 'palette' :
                              file.path.endsWith('.py') ? 'file-code' :
                              file.path === 'Dockerfile' ? 'package' :
                              'file'
                            } 
                            size={16} 
                          />
                          <span className="file-path">{file.path}</span>
                          <span className="file-size">{file.size}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="empty-state">
                    <Icon name="folder-open" size={48} />
                    <p>No files found</p>
                  </div>
                )}
              </>
            ) : selectedResource ? (
              <div className="resource-details">
                <div className="code-header">
                  <button 
                    className="back-button" 
                    onClick={() => {
                      setSelectedResource(null);
                      setSelectedResourceType(null);
                      setActiveTab('resources');
                    }}
                    type="button"
                  >
                    <Icon name="arrow-left" size={20} />
                    <span>Back to Resources</span>
                  </button>
                  <h2>{selectedResource.name}</h2>
                  <p className="resource-type-label">
                    {selectedResourceType === 'container' ? 'Container' :
                     selectedResourceType === 'endpoint' ? 'Endpoint' :
                     selectedResourceType === 'collection' ? 'Collection' :
                     selectedResourceType === 'configmap' ? 'Config Map' :
                     selectedResourceType === 'secret' ? 'Secret' : 'Resource'}
                  </p>
                </div>
                <div className="detail-section">
                  <h3>Configuration</h3>
                  <pre className="detail-json">
                    {JSON.stringify(selectedResource, null, 2)}
                  </pre>
                </div>
                <button
                  className="ask-ai-about-resource"
                  onClick={() => {
                    const resourceType = 
                      selectedResourceType === 'container' ? 'container' :
                      selectedResourceType === 'endpoint' ? 'endpoint' :
                      selectedResourceType === 'collection' ? 'collection' :
                      selectedResourceType === 'configmap' ? 'config map' :
                      selectedResourceType === 'secret' ? 'secret' : 'resource';
                    const prompt = `Can you help me with the ${resourceType} "${selectedResource.name}"? Here are the details:\n\n${JSON.stringify(selectedResource, null, 2)}`;
                    handleAskAI(prompt);
                  }}
                  type="button"
                >
                  <Icon name="message-circle" size={20} />
                  <span>Ask AI about this {selectedResourceType}</span>
                </button>
              </div>
            ) : (
              <div className="empty-state">
                <Icon name="code" size={48} />
                <p>Select a resource from the Resources tab to view its code</p>
                <button 
                  className="switch-tab-btn"
                  onClick={() => setActiveTab('resources')}
                  type="button"
                >
                  Go to Resources
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Create Service Modal */}
      {showCreateServiceModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowCreateServiceModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Service</h2>
              <button 
                className="modal-close-btn"
                onClick={() => setShowCreateServiceModal(false)}
                type="button"
              >
                <Icon name="x" size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="serviceName">Service Name *</label>
                <input
                  id="serviceName"
                  type="text"
                  value={serviceName}
                  onChange={(e) => setServiceName(e.target.value)}
                  placeholder="my-service"
                  disabled={createServiceLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="serviceDescription">Description</label>
                <textarea
                  id="serviceDescription"
                  value={serviceDescription}
                  onChange={(e) => setServiceDescription(e.target.value)}
                  placeholder="Service description..."
                  rows={3}
                  disabled={createServiceLoading}
                />
              </div>

              <div className="form-group">
                <label>Template *</label>
                {templatesLoading ? (
                  <div className="loading-state-inline">
                    <Icon name="spinner" size={20} />
                    <span>Loading templates...</span>
                  </div>
                ) : templates.length > 0 ? (
                  <div className="template-list">
                    {templates.map((template) => (
                      <button
                        key={template.id}
                        className={`template-item ${selectedTemplate?.id === template.id ? 'selected' : ''}`}
                        onClick={() => setSelectedTemplate(template)}
                        type="button"
                        disabled={createServiceLoading}
                      >
                        <div className="template-icon">
                          <Icon name="box" size={20} />
                        </div>
                        <div className="template-info">
                          <div className="template-name">{template.name}</div>
                          <div className="template-version">v{template.version?.name || template.version}</div>
                        </div>
                        {selectedTemplate?.id === template.id && (
                          <Icon name="check-circle" size={20} />
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state-inline">
                    <p>No Mia Flow templates found</p>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowCreateServiceModal(false)}
                disabled={createServiceLoading}
                type="button"
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleCreateService}
                disabled={createServiceLoading || !serviceName.trim() || !selectedTemplate}
                type="button"
              >
                {createServiceLoading ? (
                  <>
                    <Icon name="spinner" size={18} />
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <Icon name="plus" size={18} />
                    <span>Create Service</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Toast Notification */}
      {toast && (
        <div className={`mobile-toast mobile-toast-${toast.type}`}>
          <Icon name={toast.type === 'info' ? 'info' : 'alert-circle'} size={16} />
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
};

MobileCodePage.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialTab: PropTypes.oneOf(['project', 'resources', 'code']),
  onAskAI: PropTypes.func
};

export default MobileCodePage;
